import React, { useEffect, useRef, useState } from 'react'
import './index.scss'
import { Link } from 'gatsby'
import HeadTylerKaru from '@images/svg/headTylerKaru'
import { SanityHeader } from '@utils/types'
import Hamburger from '@images/svg/hamburger'

type Props = {
  header: SanityHeader
  pathname?: string
}

const HeaderComponent = ({ header, pathname }: Props) => {
  const [open, setOpen] = useState(false)
  const rootRef = useRef<HTMLElement>(null)
  const toggleRef = useRef<HTMLButtonElement>(null)

  useEffect(() => setOpen(false), [pathname])

  useEffect(() => {
    if (!open) return

    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        setOpen(false)
        toggleRef.current?.focus()
        return
      }
      if (event.key !== 'Tab') return

      const focusable = rootRef.current?.querySelectorAll<HTMLElement>(
        'a[href], button',
      )
      if (!focusable?.length) return

      const first = focusable[0]
      const last = focusable[focusable.length - 1]
      if (event.shiftKey && document.activeElement === first) {
        event.preventDefault()
        last.focus()
      } else if (!event.shiftKey && document.activeElement === last) {
        event.preventDefault()
        first.focus()
      }
    }

    const { body } = document
    const previousOverflow = body.style.overflow
    body.style.overflow = 'hidden'
    document.addEventListener('keydown', onKeyDown)

    return () => {
      body.style.overflow = previousOverflow
      document.removeEventListener('keydown', onKeyDown)
    }
  }, [open])

  return (
    <header ref={rootRef} className={`tk-header${open ? ' is-open' : ''}`}>
      <div className="tk-header__backdrop" />
      <div className="tk-header__bar">
        <Link
          className="tk-header__brand"
          to="/"
          onClick={() => setOpen(false)}
        >
          <HeadTylerKaru />
        </Link>
        <button
          ref={toggleRef}
          type="button"
          className="tk-header__toggle"
          aria-expanded={open}
          aria-controls="tk-header-menu"
          aria-label={open ? 'Close menu' : 'Open menu'}
          onClick={() => setOpen((value) => !value)}
        >
          <Hamburger />
        </button>
      </div>
      <nav id="tk-header-menu" className="tk-header__menu">
        <ul>
          {header.links.map((item, index) => (
            <li key={index}>
              <Link to={item.slug.current} onClick={() => setOpen(false)}>
                {item.title}
              </Link>
            </li>
          ))}
        </ul>
      </nav>
    </header>
  )
}

export default HeaderComponent
